import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/api";
import { PageHeader, Spinner, Card } from "@/components/common";
import IndividualDashboard from "@/pages/dashboard/IndividualDashboard";
import {
  Building2, Store, FileText, CalendarCheck, Wallet, TrendingUp,
  ShieldAlert, ClipboardCheck, ArrowRight, Database, CalendarDays,
} from "lucide-react";

const STATUS_COLORS = {
  pending_payment: "#F59E0B", confirmed: "#10B981", in_progress: "#F59E0B",
  completed: "#06B6D4", cancelled: "#F43F5E",
};

export default function Dashboard() {
  const { user, hasRole } = useAuth();
  const [stats, setStats] = useState(null);

  const ops = hasRole("ops", "finance", "admin");
  const isIndividualOnly = hasRole("individual") && !ops && !hasRole("school_coordinator", "provider");

  useEffect(() => {
    if (isIndividualOnly) return;
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, [isIndividualOnly]);

  if (isIndividualOnly) return <IndividualDashboard />;
  if (!stats) return <Spinner />;

  const cards = [
    { label: "Institutions", value: stats.institutions, icon: Building2, to: "/app/institutions" },
    { label: "Providers", value: stats.providers, icon: Store, to: "/app/providers" },
    { label: "Enquiries", value: stats.enquiries, icon: FileText, to: "/app/enquiries" },
    { label: "Bookings", value: stats.bookings, icon: CalendarCheck, to: "/app/bookings" },
  ];
  const totalPipeline = Object.values(stats.pipeline || {}).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="animate-fadeUp">
      <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle={ops ? "Operations command overview" : "Your institution's activity at a glance"} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Link key={c.label} to={c.to} data-testid={`stat-${c.label.toLowerCase()}`}
              className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-primary/50">
              <div className="flex items-center justify-between">
                <Icon className="h-6 w-6 text-primary" />
                <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
              <p className="mt-4 font-display text-4xl font-bold">{c.value ?? 0}</p>
              <p className="mt-1 text-sm text-muted-foreground">{c.label}</p>
            </Link>
          );
        })}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <h3 className="font-display text-xl font-semibold">Booking pipeline</h3>
          </div>
          {totalPipeline <= 1 && !Object.keys(stats.pipeline || {}).length ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No bookings yet.</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(stats.pipeline || {}).map(([k, v]) => (
                <div key={k}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span className="capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="font-mono text-muted-foreground">{v}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-background">
                    <div className="h-full rounded-full" style={{ width: `${(v / totalPipeline) * 100}%`, background: STATUS_COLORS[k] || "#64748B" }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div className="mb-4 flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            <h3 className="font-display text-xl font-semibold">Revenue</h3>
          </div>
          <p className="font-mono text-xs uppercase text-muted-foreground">Collected</p>
          <p className="font-display text-4xl font-bold text-emerald-400">{formatINR(stats.revenue)}</p>
          <p className="mt-4 font-mono text-xs uppercase text-muted-foreground">Outstanding</p>
          <p className="font-display text-2xl font-bold text-amber-400">{formatINR(stats.outstanding)}</p>
        </Card>
      </div>

      {ops && (
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <ActionTile icon={ShieldAlert} label="Institutions to verify" value={stats.pending_institution_verifications} to="/app/institutions" />
          <ActionTile icon={ShieldAlert} label="Providers to verify" value={stats.pending_provider_verifications} to="/app/providers" />
          <ActionTile icon={ClipboardCheck} label="Open enquiries" value={stats.open_enquiries} to="/app/enquiries" />
        </div>
      )}

      {hasRole("provider", "ops", "admin") && (
        <Link to="/app/owner-calendar" data-testid="owner-calendar-link"
          className="mt-6 flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary/50">
          <div className="rounded-xl bg-primary/10 p-3 text-primary"><CalendarDays className="h-5 w-5" /></div>
          <div className="flex-1">
            <p className="font-display text-lg font-bold">Owner Calendar</p>
            <p className="text-xs text-muted-foreground">Day/week view of bookings, approve requests and block time</p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </Link>
      )}

      {hasRole("ops", "admin") && (
        <Link to="/app/demo-data" data-testid="demo-data-link"
          className="mt-6 flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary/50">
          <div className="rounded-xl bg-primary/10 p-3 text-primary"><Database className="h-5 w-5" /></div>
          <div className="flex-1">
            <p className="font-display text-lg font-bold">Demo Data Management</p>
            <p className="text-xs text-muted-foreground">Audit &amp; safely remove sample/demo data before going live</p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </Link>
      )}
    </div>
  );
}

function ActionTile({ icon: Icon, label, value, to }) {
  return (
    <Link to={to} className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary/50">
      <div className="rounded-xl bg-primary/10 p-3 text-primary"><Icon className="h-5 w-5" /></div>
      <div>
        <p className="font-display text-2xl font-bold">{value ?? 0}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </Link>
  );
}
